import React, { useRef } from 'react';
import { Link } from 'react-router';
import { useTracker } from '../../../../../tracker';
import { useImpression } from '../../../../../tracker/src/react-bindings';
import { Image } from '@rocco/components/image';

interface AnalyticsAmplifierTileProps {
    professionalId: number;
    image: string; // Main background image
    logo: string; // Logo image
    name: string;
    subtitle: string;
    website: string;
    backgroundColor: string;
}

export const AnalyticsAmplifierTile: React.FC<AnalyticsAmplifierTileProps> = ({
    professionalId,
    image,
    logo,
    name,
    subtitle,
    website,
    backgroundColor,
}) => {
    const ref = useRef<HTMLAnchorElement>(null);
    const tracker = useTracker();

    // Track impression when the card is visible
    useImpression({
        onImpression: async (url: URL) => {
            await tracker.log('AnalyticsAmplifierImpression', {
                url: url,
                targetTracker: ['archiproTracker', 'ga4Tracker'],
                data: {
                    professionalID: professionalId,
                    type: 'AnalyticsAmplifier',
                },
            });
        },
        ref,
    });

    const handleClick = () => {
        // Track click event
        void tracker.log('AnalyticsAmplifierClick', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
            data: {
                ExtraData: JSON.stringify({
                    professionalId: professionalId,
                }),
            },
            ga4Data: {
                professionalId: professionalId,
            },
        });
    };
    return (
        <Link
            to={website}
            target="_blank"
            className={`group relative block h-full w-full overflow-hidden rounded-tile shadow-lg`}
            onClick={handleClick}
            ref={ref}
        >
            {/* Background Image */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${image})` }}
            />
            {/* Overlay */}
            <div
                className="absolute inset-0 left-0 top-0 h-full w-full"
                style={{
                    background:
                        'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 65.37%, rgba(0, 0, 0, 0.40) 100%), linear-gradient(0deg, rgba(0, 0, 0, 0.00) 63.04%, rgba(0, 0, 0, 0.40) 90.27%), rgba(0, 0, 0, 0.20)',
                }}
            />
            {/* Logo (top-left) */}
            <div className="absolute left-2.5 top-2 flex w-full items-center gap-2 md:left-4 md:top-4 md:w-[calc(100%-1.5rem)] md:gap-4">
                <div
                    className="flex size-12.5 items-center justify-center overflow-hidden rounded-full p-0.5 md:size-23"
                    style={{
                        backgroundColor: backgroundColor || 'white',
                    }}
                >
                    <Image
                        src={logo}
                        alt={`${name} logo`}
                        size="professionalLogo"
                    />
                </div>
                {/* Mobile */}
                <span className="w-24 font-display font-medium leading-[1.1] text-white md:w-[60%] md:max-w-42 md:pr-1 md:text-5 md:font-medium">
                    {name}
                </span>
            </div>
            {/* Content (bottom) */}
            <div className="absolute bottom-4 left-3.5 right-3 z-10 md:bottom-8 md:left-5 md:right-3">
                <span className="flex w-42 max-w-full font-display text-3.5 font-medium leading-[1.1] text-white md:w-62 md:text-5 md:font-medium">
                    {subtitle}
                </span>
            </div>
        </Link>
    );
};
